import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Rocket, Handshake, LineChart, ShieldCheck, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { PUBLIC_IMAGES } from '../lib/publicImages';
import { EditorialImageCard } from '../components/EditorialImageCard';

const highlights = [
  {
    icon: Rocket,
    title: 'Escala sin ampliar estructura',
    description: 'Vende IA, automatización, contenido y estrategia como una línea premium sin contratar un equipo completo adicional.'
  },
  {
    icon: Handshake,
    title: 'Modelo colaborativo',
    description: 'Tu agencia mantiene la relación con cliente y Neuralia369 aporta ejecución, sistema y capacidad de entrega.'
  },
  {
    icon: LineChart,
    title: 'Pricing especial para agencias',
    description: 'Accede a condiciones más potentes para reventa, paquetes premium y crecimiento comercial sostenible.'
  },
  {
    icon: ShieldCheck,
    title: 'Operación sólida',
    description: 'Roles, aprobaciones, auditoría, timeline y automatización para trabajar sin caos ni pérdidas de control.'
  }
];

export default function AgencyPage() {
  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl" data-testid="agency-page">
      <SEO
        title="Programa para Agencias | Neuralia369"
        description="Solución premium para agencias de marketing y publicidad: IA, automatización, contenido, pricing y operación robusta."
        path="/agencias"
      />

      <section className="relative overflow-hidden py-golden-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-golden-lg">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Programa Agencia Pro</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-golden-md">
              Una línea premium de <span className="gradient-text">IA y automatización para agencias</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-text-body max-w-3xl mb-golden-lg">
              Si tu agencia quiere vender más sin inflar estructura, Neuralia369 puede ser la capa de producción, criterio y automatización que te faltaba.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-3">
              <Link to="/precios?segment=agencia" className="btn-primary" data-testid="agency-pricing-cta">
                <Sparkles className="w-4 h-4" /> Ver pricing agencia
              </Link>
              <Link to="/acceso-agencias" className="btn-secondary" data-testid="agency-access-cta">
                <ShieldCheck className="w-4 h-4" /> Acceso agencias
              </Link>
              <Link to="/agenda" className="btn-secondary" data-testid="agency-demo-cta">
                Agenda una demo
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid md:grid-cols-2 gap-golden-lg">
          {highlights.map((item, index) => (
            <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="glass-card p-golden-lg">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-golden-md">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl mb-2">{item.title}</h3>
              <p className="text-text-body">{item.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg mt-golden-xl">
          <EditorialImageCard
            image={PUBLIC_IMAGES.agencies.hero}
            alt="Equipo de agencia colaborando con herramientas y estrategia"
            kicker="Agencias con más capacidad"
            title="Una estructura premium para vender más sin inflar equipo"
            description="La imagen refuerza el mensaje: colaboración real, criterio comercial y un entorno de trabajo que se ve sólido y profesional."
            testId="agency-editorial-image"
            minHeight="min-h-[23rem]"
          />
        </div>
      </section>
    </div>
  );
}